import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LanguageSelector from '../components/LanguageSelector';
import { APP_NAME } from '../lib/appConfig';
import { useLanguage } from '../i18n';
import { 
  Users, Shield, Wallet, ArrowRight, Check, 
  Globe, Smartphone, MessageCircle, TrendingUp,
  Lock, Clock
} from 'lucide-react';

export const LandingPage = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Users,
      title: t('landing.features.group.title'),
      description: t('landing.features.group.description'),
    },
    {
      icon: Shield,
      title: t('landing.features.security.title'),
      description: t('landing.features.security.description'),
    },
    {
      icon: Wallet,
      title: t('landing.features.payments.title'),
      description: t('landing.features.payments.description'),
    },
    {
      icon: MessageCircle,
      title: t('landing.features.coach.title'),
      description: t('landing.features.coach.description'),
    },
    {
      icon: Globe,
      title: t('landing.features.currencies.title'),
      description: t('landing.features.currencies.description'),
    },
    {
      icon: TrendingUp,
      title: t('landing.features.dashboard.title'),
      description: t('landing.features.dashboard.description'),
    },
  ];

  const tontineTypes = [
    {
      type: 'ROSCA',
      title: t('landing.types.rosca.title'),
      description: t('landing.types.rosca.description'),
      color: 'bg-emerald-500',
    },
    {
      type: 'ASCA',
      title: t('landing.types.asca.title'),
      description: t('landing.types.asca.description'),
      color: 'bg-orange-500',
    },
    {
      type: 'Hybride',
      title: t('landing.types.hybrid.title'),
      description: t('landing.types.hybrid.description'),
      color: 'bg-blue-500',
    },
  ];

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass dark:glass-dark">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-10 h-10 bg-gradient-to-br from-stone-900 to-stone-700 dark:from-stone-100 dark:to-stone-300 rounded-xl flex items-center justify-center">
                <span className="text-white dark:text-stone-900 font-display font-bold text-lg">T</span>
              </div>
              <span className="font-display font-bold text-xl text-stone-900 dark:text-stone-50 whitespace-nowrap max-w-[220px] truncate">{APP_NAME}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSelector />
              <Link to="/auth" data-testid="nav-login-btn">
                <Button variant="ghost" className="text-stone-700 dark:text-stone-300 hidden sm:inline-flex">
                  {t('landing.navLogin')}
                </Button>
              </Link>
              <Link to="/auth" data-testid="nav-register-btn">
                <Button className="btn-primary px-3 sm:px-4 text-sm sm:text-base">
                  {t('landing.navStart')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <p className="uppercase tracking-[0.2em] text-xs font-semibold text-orange-700 dark:text-orange-500 mb-4">
                  {t('landing.heroTagline')}
                </p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 dark:text-stone-50 leading-[1.1]">
                  {t('landing.heroTitlePrefix')}
                  <span className="text-orange-700 dark:text-orange-500"> {t('landing.heroTitleHighlight')}</span>
                </h1>
              </div>
              <p className="text-lg text-stone-600 dark:text-stone-400 max-w-lg">
                {t('landing.heroDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/auth" data-testid="hero-cta-btn">
                  <Button className="btn-accent text-lg px-10 py-6 w-full sm:w-auto">
                    {t('landing.heroPrimaryCta')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#features" data-testid="hero-learn-more-btn">
                  <Button variant="outline" className="btn-secondary text-lg px-10 py-6 w-full sm:w-auto">
                    {t('landing.heroSecondaryCta')}
                  </Button>
                </a>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-stone-600 dark:text-stone-400">{t('landing.freeToStart')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-stone-600 dark:text-stone-400">{t('landing.secureData')}</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1680878898191-7c8a1e3b8180?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt={t('landing.imageAlt')}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent" />
              </div>
              {/* Floating stats card */}
              <Card className="hidden sm:block absolute -bottom-6 -left-6 shadow-xl animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">+2.5M</p>
                      <p className="text-sm text-stone-500">{t('landing.savedFcfa')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tontine Types */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-stone-100 dark:bg-stone-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-left mb-12">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-orange-700 dark:text-orange-500 mb-4">
              {t('landing.typesEyebrow')}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-50">
              {t('landing.typesTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {tontineTypes.map((item, index) => (
              <Card 
                key={item.type} 
                className="card-neo hover:-translate-y-1 transition-transform duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`tontine-type-${item.type.toLowerCase()}`}
              >
                <CardContent className="p-6">
                  <div className={`w-3 h-3 rounded-full ${item.color} mb-4`} />
                  <p className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1">
                    {item.type}
                  </p>
                  <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-50 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-stone-600 dark:text-stone-400">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-left mb-12">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-orange-700 dark:text-orange-500 mb-4">
              {t('landing.featuresEyebrow')}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-50">
              {t('landing.featuresTitle')}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={feature.title} 
                className="card-neo group"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-stone-100 dark:bg-stone-800 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 transition-colors">
                    <feature.icon className="w-6 h-6 text-stone-700 dark:text-stone-300 group-hover:text-orange-700 dark:group-hover:text-orange-500 transition-colors" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-stone-900 dark:text-stone-50 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-stone-600 dark:text-stone-400 text-sm">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-stone-900 dark:bg-stone-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-lg text-stone-400 mb-8 max-w-2xl mx-auto">
            {t('landing.ctaDescription', { appName: APP_NAME })}
          </p>
          <Link to="/auth" data-testid="cta-start-btn">
            <Button className="btn-accent text-lg px-12 py-6">
              {t('landing.ctaButton')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-stone-200 dark:border-stone-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-stone-900 to-stone-700 dark:from-stone-100 dark:to-stone-300 rounded-lg flex items-center justify-center">
                <span className="text-white dark:text-stone-900 font-display font-bold text-sm">T</span>
              </div>
              <span className="font-display font-bold text-stone-900 dark:text-stone-50">{APP_NAME}</span>
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('landing.footerCopyright', { appName: APP_NAME, year: '2026' })}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="#" className="text-sm text-stone-500 hover:text-stone-900 dark:hover:text-stone-50 transition-colors">
                {t('landing.footerPrivacy')}
              </a>
              <a href="#" className="text-sm text-stone-500 hover:text-stone-900 dark:hover:text-stone-50 transition-colors">
                {t('landing.footerTerms')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
